// src/pages/private/settings/securityTabs/SecuritySessions.jsx

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DesktopOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../authContext';
import loadMoment from '../../../../../utils/loadMoment';

function SecuritySessions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { timezone } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [renamingGroupKey, setRenamingGroupKey] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const formatActivityDate = useCallback(async (value) => {
    if (!value) return t('securitySessionsUnknownActivity');
    const moment = await loadMoment();
    const tz = timezone || moment.tz.guess();
    return moment(value).tz(tz).format('DD/MM/YYYY HH:mm');
  }, [timezone, t]);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/user/sessions', { withCredentials: true });
      const nextDevices = Array.isArray(response.data?.devices) ? response.data.devices : [];
      const formattedDevices = await Promise.all(
        nextDevices.map(async (device) => ({
          ...device,
          formattedLastActivity: await formatActivityDate(device.lastActivity),
        }))
      );
      setDevices(formattedDevices);
    } catch (err) {
      console.error('[SecuritySessions] load failed:', err);
      setError(t('securitySessionsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [formatActivityDate, t]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleLogoutRedirect = () => {
    navigate('/login');
  };

  const getGroupKey = (device) =>
    device.deviceId || device.sessions?.[0]?.sessionId || '';

  const getSessionIds = (device) =>
    (device.sessions || [])
      .map((session) => session.sessionId)
      .filter(Boolean);

  const handleRevokeDevice = async (device) => {
    const confirmMessage = device.isCurrentDevice
      ? t('securitySessionsRevokeCurrentConfirm')
      : t('securitySessionsRevokeConfirm');

    if (!window.confirm(confirmMessage)) return;

    setActionError('');
    setBusyKey(device.deviceId || device.sessions?.[0]?.sessionId);

    try {
      if (device.deviceId) {
        const response = await axios.delete(
          `/api/user/devices/${device.deviceId}/sessions`,
          { withCredentials: true }
        );

        if (response.data?.isCurrentDevice) {
          handleLogoutRedirect();
          return;
        }
      } else {
        const sessionIds = getSessionIds(device);
        if (!sessionIds.length) return;

        let revokedCurrent = false;
        for (const sessionId of sessionIds) {
          const response = await axios.delete(
            `/api/user/sessions/${sessionId}`,
            { withCredentials: true }
          );
          if (response.data?.isCurrent) {
            revokedCurrent = true;
          }
        }

        if (revokedCurrent) {
          handleLogoutRedirect();
          return;
        }
      }

      await loadSessions();
    } catch (err) {
      console.error('[SecuritySessions] revoke failed:', err);
      setActionError(t('securitySessionsRevokeError'));
    } finally {
      setBusyKey(null);
    }
  };

  const handleRevokeOthers = async () => {
    const otherCount = devices.filter((device) => !device.isCurrentDevice).length;
    if (otherCount === 0) {
      setActionError(t('securitySessionsNoOtherSessions'));
      return;
    }

    if (!window.confirm(t('securitySessionsRevokeOthersConfirm'))) return;

    setActionError('');
    setBusyKey('others');

    try {
      await axios.delete('/api/user/sessions/others', { withCredentials: true });
      await loadSessions();
    } catch (err) {
      console.error('[SecuritySessions] revoke others failed:', err);
      setActionError(t('securitySessionsRevokeError'));
    } finally {
      setBusyKey(null);
    }
  };

  const startRename = (device) => {
    setRenamingGroupKey(getGroupKey(device));
    setRenameDraft(device.deviceName || '');
    setActionError('');
  };

  const cancelRename = () => {
    setRenamingGroupKey(null);
    setRenameDraft('');
  };

  const saveRename = async (device) => {
    const trimmed = renameDraft.trim();
    if (!trimmed) return;

    const groupKey = getGroupKey(device);
    setActionError('');
    setBusyKey(`rename:${groupKey}`);

    try {
      if (device.deviceId) {
        await axios.patch(
          `/api/user/devices/${device.deviceId}`,
          { deviceName: trimmed },
          { withCredentials: true }
        );
      } else {
        const sessionIds = getSessionIds(device);
        if (!sessionIds.length) return;

        await axios.patch(
          '/api/user/sessions/device-name',
          { deviceName: trimmed, sessionIds },
          { withCredentials: true }
        );
      }

      cancelRename();
      await loadSessions();
    } catch (err) {
      console.error('[SecuritySessions] rename failed:', err);
      setActionError(t('securitySessionsRenameError'));
    } finally {
      setBusyKey(null);
    }
  };

  const hasOtherDevices = devices.some((device) => !device.isCurrentDevice);

  return (
    <section className="security-sessions" aria-labelledby="security-sessions-heading">
      <header className="user-settings-section security-page__section-heading">
        <h3 id="security-sessions-heading" className="settings-h3">
          {t('SecuritySessionsHeading')}
        </h3>
      </header>

      <div className="security-card security-card--sessions">
        <div className="security-card--sessions-header">
          <div className="security-card__lead">
            <div className="security-card__icon-wrap">
              <DesktopOutlined className="security-card__icon" aria-hidden />
            </div>
            <div className="security-card__copy">
              <h4 className="security-card__title">{t('securityCardSessionsTitle')}</h4>
              <p className="security-card__subtitle">{t('securityCardSessionsDesc')}</p>
            </div>
          </div>
          <div className="security-card__cta">
            <button
              type="button"
              className="btn-style1 btn-security-submit"
              onClick={handleRevokeOthers}
              disabled={loading || !hasOtherDevices || busyKey === 'others'}
              data-resource="private.settings.security"
              data-action="delete"
              data-route="/api/user/sessions/others"
            >
              {busyKey === 'others' ? '…' : t('securitySessionsRevokeOthers')}
            </button>
          </div>
        </div>

        {error && <p className="security-card__alert">{error}</p>}
        {actionError && <p className="security-card__alert">{actionError}</p>}

        {loading ? (
          <p className="security-sessions__status">{t('securitySessionsLoading')}</p>
        ) : (
          <>
            {devices.length > 0 && (
              <div className="security-sessions__separator security-sessions__separator--header" aria-hidden="true" />
            )}
            <div className="security-sessions__list">
              {devices.map((device, index) => {
                const groupKey = getGroupKey(device);
                const rowKey = groupKey;
                const isRenaming = renamingGroupKey === groupKey;
                const isBusy = busyKey === rowKey || busyKey === `rename:${groupKey}`;
                const locationText = device.location
                  ? `${device.location} (${t('securitySessionsApproxLocation')})`
                  : t('securitySessionsUnknownLocation');
                const ipText = device.ipAddress || t('securitySessionsUnknownIp');
                const sessionCount = device.sessionCount || device.sessions?.length || 1;

                return (
                  <React.Fragment key={rowKey}>
                    {index > 0 && (
                      <div className="security-sessions__separator" aria-hidden="true" />
                    )}
                    <div
                      className={`security-sessions__item${
                        device.isCurrentDevice ? ' security-sessions__item--current' : ''
                      }`}
                    >
                      <div className="security-sessions__device-main">
                        <div className="security-sessions__device-title-row">
                          <h4 className="security-sessions__device-title">
                            {device.displayName || device.label}
                          </h4>
                          {device.isCurrentDevice && (
                            <span className="security-sessions__badge">
                              {t('securitySessionsCurrentDevice')}
                            </span>
                          )}
                        </div>

                        {device.deviceName && (
                          <p className="security-sessions__meta">
                            {device.label}
                          </p>
                        )}

                        {sessionCount > 1 && (
                          <p className="security-sessions__meta">
                            {t('securitySessionsActiveCount', { count: sessionCount })}
                          </p>
                        )}

                        <p className="security-sessions__meta">
                          {t('securitySessionsIp')}: {ipText}
                          {' — '}
                          {t('securitySessionsLocation')}: {locationText}
                          {' — '}
                          {t('securitySessionsLastActive')}: {device.formattedLastActivity}
                        </p>

                        <div className="security-sessions__rename">
                          {isRenaming ? (
                            <div className="security-sessions__rename-form">
                              <input
                                type="text"
                                className="security-card__input security-sessions__rename-input"
                                value={renameDraft}
                                maxLength={64}
                                placeholder={t('securitySessionsRenamePlaceholder')}
                                onChange={(ev) => setRenameDraft(ev.target.value)}
                                aria-label={t('securitySessionsRenamePlaceholder')}
                              />
                              <button
                                type="button"
                                className="btn-style1 btn-security-submit"
                                onClick={() => saveRename(device)}
                                disabled={!renameDraft.trim() || busyKey === `rename:${groupKey}`}
                                data-resource="private.settings.security"
                                data-action="write"
                                data-route={
                                  device.deviceId
                                    ? `/api/user/devices/${device.deviceId}`
                                    : '/api/user/sessions/device-name'
                                }
                              >
                                {busyKey === `rename:${groupKey}` ? '…' : t('securitySessionsRenameSave')}
                              </button>
                              <button
                                type="button"
                                className="btn-style1 btn-security-submit security-sessions__rename-cancel"
                                onClick={cancelRename}
                              >
                                {t('securitySessionsRenameCancel')}
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="security-sessions__rename-link"
                              onClick={() => startRename(device)}
                              data-resource="private.settings.security"
                              data-action="write"
                              data-route={
                                device.deviceId
                                  ? `/api/user/devices/${device.deviceId}`
                                  : '/api/user/sessions/device-name'
                              }
                            >
                              {t('securitySessionsRename')}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="security-sessions__item-actions">
                        <button
                          type="button"
                          className="btn-style1 delete red"
                          onClick={() => handleRevokeDevice(device)}
                          disabled={isBusy}
                          data-resource="private.settings.security"
                          data-action="delete"
                          data-route={
                            device.deviceId
                              ? `/api/user/devices/${device.deviceId}/sessions`
                              : `/api/user/sessions/${device.sessions?.[0]?.sessionId}`
                          }
                        >
                          {isBusy ? '…' : t('securitySessionsRevoke')}
                        </button>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {!devices.length && !error && (
                <p className="security-sessions__status">{t('securitySessionsEmpty')}</p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default SecuritySessions;
